import {
  PlanStep,
  RequestThreadStatus,
  StepStatus,
  StepStatusReason,
} from '../types/generated/graphql';

/**
 * Panel modes for the Task (right) panel.
 * At any moment, the panel should answer one question: "What should the user do next?"
 */
export type StepPanelMode =
  | 'ACTIVE_WORK'
  | 'WAITING_ON_DELEGATE'
  | 'READY_TO_COMPLETE'
  | 'COMPLETED'
  | 'WAITING_EXTERNAL'
  | 'BLOCKED_CONNECTOR'
  | 'BLOCKED_CONNECTION_REQUIRED';

/**
 * Mode colors for visual hierarchy.
 */
export const PANEL_MODE_COLORS = {
  ACTIVE_WORK: 'blue',
  WAITING_ON_DELEGATE: 'amber',
  READY_TO_COMPLETE: 'green',
  COMPLETED: 'green',
  WAITING_EXTERNAL: 'amber',
  BLOCKED_CONNECTOR: 'red',
  BLOCKED_CONNECTION_REQUIRED: 'amber',
} as const;

/**
 * The set of `StepStatusReason` values that indicate the step is
 * blocked specifically on a missing or broken integration
 * connection. Centralized so the panel-mode router and the
 * `ConnectionRequiredContent` reason chip stay in lockstep.
 */
const CONNECTION_REQUIRED_REASONS: ReadonlySet<StepStatusReason> = new Set([
  StepStatusReason.ConnectionRequired,
  StepStatusReason.OrgReconnectRequired,
  StepStatusReason.ConsumerWithheldFallback,
  // S11 — preview mode landed on a creator USER miss. We route this
  // through the same panel mode as the live JIT Connect flow because
  // the affordance is identical (run a broker Connect popup); the
  // ConnectionRequiredContent component branches on
  // `statusReason === PreviewNeedsUser` to swap the copy without
  // duplicating panel-mode plumbing.
  StepStatusReason.PreviewNeedsUser,
]);

/**
 * Centralized logic for determining the current panel mode.
 * This is the single source of truth - no scattered conditionals.
 *
 * Priority order:
 * 1. COMPLETED - step is done, show read-only summary
 * 2. WAITING_ON_DELEGATE - active delegation in progress
 * 3. WAITING_EXTERNAL - async connector in progress (e.g. FOIA), lock UI
 * 4. BLOCKED_CONNECTOR - async connector failed/blocked, show reason + lock UI
 * 5. READY_TO_COMPLETE - all requirements met, show completion CTA
 * 6. ACTIVE_WORK - default, user is working on the step
 */
export function getStepPanelMode(step: PlanStep | null): StepPanelMode {
  if (!step) return 'ACTIVE_WORK';

  // Check if already completed - highest priority, show read-only view
  if (step.status === StepStatus.Completed) return 'COMPLETED';

  // Check for active delegation (OPEN or READY status = waiting on delegate)
  const hasActiveDelegate = step.requestThreads?.some(
    (t) =>
      t.status === RequestThreadStatus.Open ||
      t.status === RequestThreadStatus.Ready
  );
  if (hasActiveDelegate) return 'WAITING_ON_DELEGATE';

  // Async connector waiting for external response - lock form and show activity
  if (step.status === StepStatus.WaitingExternal) return 'WAITING_EXTERNAL';

  // S4: blocked specifically on a missing/broken integration connection
  // — render the JIT Connect card. This must precede the generic
  // BLOCKED_CONNECTOR check because some legacy code paths populate
  // both `statusReason` and `context.connectorWait` and the JIT Connect
  // copy is the higher-signal action for the user.
  if (
    step.status === StepStatus.Blocked &&
    step.statusReason &&
    CONNECTION_REQUIRED_REASONS.has(step.statusReason)
  ) {
    return 'BLOCKED_CONNECTION_REQUIRED';
  }

  // Async connector blocked (e.g. poll failed, max attempts) - show reason and lock
  const ctx = step.context as { connectorWait?: unknown; connectorPollResult?: unknown } | null | undefined;
  if (step.status === StepStatus.Blocked && (ctx?.connectorWait || ctx?.connectorPollResult)) {
    return 'BLOCKED_CONNECTOR';
  }

  // Check if ready to complete (assessment says eligible)
  if (step.assessment?.eligibleForCompletion) return 'READY_TO_COMPLETE';

  // Default: user is actively working on the step
  return 'ACTIVE_WORK';
}

/**
 * Get the active delegation thread (first non-satisfied, non-cancelled thread).
 */
export function getActiveDelegation(step: PlanStep | null) {
  if (!step?.requestThreads) return null;

  return step.requestThreads.find(
    (t) =>
      t.status === RequestThreadStatus.Open ||
      t.status === RequestThreadStatus.Ready
  ) || null;
}

/**
 * Check if step has any completed delegations.
 */
export function hasCompletedDelegation(step: PlanStep | null): boolean {
  if (!step?.requestThreads) return false;

  return step.requestThreads.some(
    (t) => t.status === RequestThreadStatus.Satisfied
  );
}
